/**
 * The period basis of a model's result, derived from the model ALONE.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT "EVALUATING ON WRITE", WHICH THE WRITE CONTRACT REFUSES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `role-coverage-authoring-input.schema.ts` closes with **"Do not 'fix' this by
 * evaluating on write"**, and that refusal stands. Read what it refuses: a
 * model's VALUE. Its argument is that a value needs the organisation's
 * working-time model — which a Role may legitimately be authored before its
 * organisation has stated — and that a measured input can move the result
 * AFTER the write. Both are true, and both are statements about magnitudes.
 *
 * A period basis is neither. Look at what produces one in
 * `coverage-evaluator.ts`:
 *
 *     periodBasis: declaration.perPeriod === undefined ? ABSOLUTE : ANNUAL
 *
 * That reads one authored field. It never reads `session.context`, never reads
 * `measuredInputs`, and never reads a magnitude. From there `mul` ADDS the
 * exponent, `div` SUBTRACTS it, and `add`/`sub`/`min`/`max` require it equal —
 * all pure integer arithmetic over the shape of the tree. So the basis of a
 * model's result is a function of the stored JSON and of nothing else: the same
 * class of fact as "does this reference resolve", which that boundary already
 * checks and calls STRUCTURE.
 *
 * The header's own words are the test, and this passes it: this module can
 * answer before the organisation has stated a working day, it answers the same
 * thing forever, and no measurement can move its answer.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE SECOND IMPLEMENTATION, AND WHAT KEEPS IT HONEST
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 🚨 The rules below are a SECOND reading of the same axis the evaluator
 * already walks, and that is the failure mode this repository has recorded more
 * than once — two implementations of one rule agree on the day they are written
 * and diverge afterwards, silently.
 *
 * It is a second implementation ON PURPOSE, because the first one cannot be
 * called here: `evaluateCoverageFormula` demands a `ValidatedCoverageContext`,
 * which is exactly the dependency the write boundary refuses to take. Extracting
 * the basis walk out of the evaluator would be the structurally better answer
 * and it is a change to the engine every coverage figure rests on.
 *
 * What keeps the two in step is not this comment.
 * `coverage-formula-period-basis.test.ts` runs BOTH over one corpus and
 * requires them to agree on every model, in both directions — a `derived` basis
 * must equal the evaluator's `quantity.periodBasis`, and every `underivable`
 * reason must correspond to the evaluator failing with the code of the same
 * name. Its anti-vacuity control is a mutation: change one rule here and that
 * spec reds.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY AN `underivable` ARM RATHER THAN A NUMBER OR A NULL
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A model can be structurally basis-less: summing an absolute quantity with a
 * per-year one, or multiplying past `year^6`. Both are models the READ already
 * refuses by name, and neither is this module's business to refuse a second
 * time at a different layer with a different message.
 *
 * A bare `number | null` would let a caller write `basis === null` and mean
 * three different things. The union forces a caller to say which of "this model
 * has a basis" and "this model has no basis to compare" it is acting on, and
 * the one caller there is handles both arms explicitly.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THE WALK CARRIES THE DIMENSION TOO
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 🔴 THE DIMENSION IS NOT A BONUS FIELD — WITHOUT IT THE CROSS-ROW CALLER
 * PRODUCES FALSE REFUSALS, AND A FALSE REFUSAL ON THIS FEATURE MAKES A ROLE
 * UNEDITABLE. `evaluateImpactMoneyTerm` refuses a term whose result is not
 * MONEY, by a different name (`*_MONEY_WRONG_DIMENSION`), and a refused term
 * never fixes the Role's money window. A walk blind to the dimension therefore
 * lets a broken model set an anchor the READ would have ignored — and
 * `coverage-money-basis-across-rows.ts` compares two such walks, so a phantom
 * anchor turns an author's perfectly consistent write into a 400.
 *
 * Reachable, not theoretical: a Role whose cost model evaluates to hours,
 * ANNUAL, beside one ABSOLUTE impact revenue model. The read anchors ABSOLUTE
 * and reports nothing; a basis-only walk anchors ANNUAL and counts one
 * disagreement, so adding a SECOND, agreeing ABSOLUTE model reads as widening.
 *
 * The dimension algebra is not reimplemented here. `multiplyCoverageDimensions`,
 * `divideCoverageDimensions`, `coverageDimensionsEqual` and
 * `isRepresentableCoverageDimension` are the evaluator's own functions, called
 * in the evaluator's own order — dimension before basis in every fold, because
 * that order decides WHICH code a broken model fails with and the parity test
 * pairs the two implementations by that code.
 */
import {
  type CoverageDimension,
  coverageDimensionsEqual,
  divideCoverageDimensions,
  isRepresentableCoverageDimension,
  multiplyCoverageDimensions
} from "./coverage-dimension.types";
import type { CoverageFormula, CoverageRef, CoverageStep } from "./coverage-formula.schema";
import {
  COVERAGE_PERIOD_BASIS_ABSOLUTE,
  COVERAGE_PERIOD_BASIS_ANNUAL,
  isRepresentableCoveragePeriodBasis
} from "./coverage-period.types";

/**
 * Why a model produces no result shape at all.
 *
 * Each arm is spelled exactly as the `CoverageEvaluationFailureCode` the
 * evaluator raises for the same model, so the parity test can pair them by name
 * rather than by a translation table that could itself drift.
 */
export type CoverageFormulaPeriodBasisRefusal =
  /** `add`/`sub`/`min`/`max` over operands measuring different THINGS. */
  | "DIMENSION_MISMATCH"
  /** A product or quotient took a dimension exponent outside `[-6, +6]`. */
  | "EXPONENT_OUT_OF_RANGE"
  /** `add`/`sub`/`min`/`max` over operands counted over different windows. */
  | "PERIOD_BASIS_MISMATCH"
  /** A product or quotient took the period exponent outside `[-6, +6]`. */
  | "PERIOD_BASIS_OUT_OF_RANGE"
  /**
   * A reference did not resolve.
   *
   * Unreachable for a formula that came out of `CoverageFormulaSchema`, which
   * proves every reference resolves and that a step names only strictly earlier
   * steps. Kept so this function is total, exactly as the evaluator keeps its
   * own arm of the same name.
   */
  | "UNRESOLVED_REFERENCE";

/** The period basis of a model's result, or the named reason there is none. */
export type CoverageFormulaPeriodBasis =
  | { readonly kind: "derived"; readonly basis: number }
  | { readonly kind: "underivable"; readonly reason: CoverageFormulaPeriodBasisRefusal };

/**
 * What a model's result IS, structurally — what it measures and over what
 * window — derived from the stored JSON alone.
 *
 * The superset of {@link CoverageFormulaPeriodBasis}: the same walk answers
 * both, so the two can never disagree about whether a model has a basis.
 */
export type CoverageFormulaResultShape =
  | {
      readonly kind: "derived";
      readonly basis: number;
      readonly dimension: CoverageDimension;
    }
  | { readonly kind: "underivable"; readonly reason: CoverageFormulaPeriodBasisRefusal };

const UNRESOLVED: CoverageFormulaResultShape = {
  kind: "underivable",
  reason: "UNRESOLVED_REFERENCE"
};

/** One operand's structural shape as the walk carries it. */
interface DerivedShape {
  readonly basis: number;
  readonly dimension: CoverageDimension;
}

/**
 * Derive the period basis of `formula`'s result without evaluating it.
 *
 * A projection of {@link coverageFormulaResultShape}, kept because the
 * within-body rule (`coverage-money-basis-agreement.ts`) asks only this half
 * and reads better for saying so.
 */
export function coverageFormulaPeriodBasis(formula: CoverageFormula): CoverageFormulaPeriodBasis {
  const shape = coverageFormulaResultShape(formula);
  return shape.kind === "derived" ? { kind: "derived", basis: shape.basis } : shape;
}

/**
 * Derive what `formula`'s result measures, and over what window, without
 * evaluating it.
 *
 * Total: every model produces one of the two arms, and nothing throws. The
 * argument is the stored shape rather than a `ValidatedCoverageFormula`,
 * because one caller runs INSIDE the schema that mints validity and so has no
 * branded value to hand over yet.
 */
export function coverageFormulaResultShape(formula: CoverageFormula): CoverageFormulaResultShape {
  const inputShapes = new Map<string, DerivedShape>();
  for (const input of formula.inputs) {
    // The one place a shape enters a model, and the whole of this module's
    // dependence on the authored JSON. Mirrors `bindInput` in the evaluator,
    // which likewise carries the author's declared dimension through unchanged
    // — a measured binding replaces the MAGNITUDE and nothing else.
    inputShapes.set(input.key, {
      basis:
        input.perPeriod === undefined
          ? COVERAGE_PERIOD_BASIS_ABSOLUTE
          : COVERAGE_PERIOD_BASIS_ANNUAL,
      dimension: input.dimension
    });
  }

  const stepShapes: DerivedShape[] = [];
  const resolve = (ref: CoverageRef): DerivedShape | undefined =>
    ref.kind === "input" ? inputShapes.get(ref.key) : stepShapes[ref.index];

  for (const step of formula.steps) {
    const operands: DerivedShape[] = [];
    for (const arg of step.args) {
      const shape = resolve(arg);
      if (shape === undefined) {
        return UNRESOLVED;
      }
      operands.push(shape);
    }

    const outcome = applyStep(step, operands);
    if (outcome.kind === "underivable") {
      return outcome;
    }
    stepShapes.push({ basis: outcome.basis, dimension: outcome.dimension });
  }

  const result = resolve(formula.result);
  return result === undefined
    ? UNRESOLVED
    : { kind: "derived", basis: result.basis, dimension: result.dimension };
}

/**
 * One step's result shape, by the same rules `applyFunction` applies to a
 * quantity.
 *
 * The switch is exhaustive over `CoverageFunction`, so a seventh function is a
 * compile error here rather than a step this walk silently mis-reads — which is
 * the drift the parity test cannot catch, because a new function has no case in
 * either implementation until somebody writes one.
 */
function applyStep(
  step: CoverageStep,
  operands: readonly DerivedShape[]
): CoverageFormulaResultShape {
  switch (step.fn) {
    case "add":
    case "sub":
    case "min":
    case "max":
      return foldSameDimension(operands);
    case "mul":
      return foldProduct(operands);
    case "div":
      return divide(operands[0], operands[1]);
  }
}

/**
 * `add`/`sub`/`min`/`max`: every operand must measure the same thing over the
 * same window.
 *
 * DIMENSION FIRST, exactly as `foldSameDimension` in the evaluator checks it,
 * and on the SAME pair. A model wrong on both axes has to fail with the
 * dimension code in both implementations or the parity test is comparing two
 * different questions.
 */
function foldSameDimension(operands: readonly DerivedShape[]): CoverageFormulaResultShape {
  const accumulator = operands[0];
  for (let i = 1; i < operands.length; i += 1) {
    const next = operands[i];
    if (!coverageDimensionsEqual(accumulator.dimension, next.dimension)) {
      return { kind: "underivable", reason: "DIMENSION_MISMATCH" };
    }
    if (accumulator.basis !== next.basis) {
      return { kind: "underivable", reason: "PERIOD_BASIS_MISMATCH" };
    }
  }
  return { kind: "derived", basis: accumulator.basis, dimension: accumulator.dimension };
}

/**
 * A product's shape, folded LEFT TO RIGHT with both ranges checked on every
 * intermediate — not summed and checked once.
 *
 * 🚨 The two are not the same function, and the difference is only reachable
 * once a `div` has produced a POSITIVE basis. `mul` over bases `+6, +1, -6`
 * totals `+1`, which is representable, while its partial sums pass through
 * `+7`, which is not — `foldProduct` in the evaluator refuses that model and a
 * one-shot sum would accept it. Inputs alone can only be `0` or `-1`, so no
 * corpus built from inputs can tell the two apart; the parity test carries the
 * `div`-fed case deliberately for that reason.
 */
function foldProduct(operands: readonly DerivedShape[]): CoverageFormulaResultShape {
  let accumulator = operands[0];
  for (let i = 1; i < operands.length; i += 1) {
    const dimension = multiplyCoverageDimensions(accumulator.dimension, operands[i].dimension);
    if (!isRepresentableCoverageDimension(dimension)) {
      return { kind: "underivable", reason: "EXPONENT_OUT_OF_RANGE" };
    }
    const basis = accumulator.basis + operands[i].basis;
    if (!isRepresentableCoveragePeriodBasis(basis)) {
      return { kind: "underivable", reason: "PERIOD_BASIS_OUT_OF_RANGE" };
    }
    accumulator = { basis, dimension };
  }
  return { kind: "derived", basis: accumulator.basis, dimension: accumulator.dimension };
}

/**
 * A quotient's shape.
 *
 * `DIVISION_BY_ZERO` has no arm here and cannot: it is a statement about a
 * MAGNITUDE, which is exactly what this module refuses to read. A model the
 * evaluator refuses for that reason is `derived` here, and the parity test's
 * corpus therefore avoids zero denominators rather than asserting agreement it
 * cannot have.
 */
function divide(numerator: DerivedShape, denominator: DerivedShape): CoverageFormulaResultShape {
  const dimension = divideCoverageDimensions(numerator.dimension, denominator.dimension);
  if (!isRepresentableCoverageDimension(dimension)) {
    return { kind: "underivable", reason: "EXPONENT_OUT_OF_RANGE" };
  }
  const basis = numerator.basis - denominator.basis;
  return isRepresentableCoveragePeriodBasis(basis)
    ? { kind: "derived", basis, dimension }
    : { kind: "underivable", reason: "PERIOD_BASIS_OUT_OF_RANGE" };
}
